
import { Component, Vue } from 'vue-property-decorator'
import { TixTime } from '@/TixTime/TixTime'
import store from '@/store/store'
import type { TTStringFormat } from '@/TixTime/helpers'
import { isCurrentOrPastMember, membershipIsRenewable, membershipRenewableFrom } from '@/state/Membership'
import { mapGetters } from 'vuex'
import { API } from '@/api/API'
import { firstEntity } from '@/api/Helpers'
import { environment } from '@/helpers/Environment'
import type { EventWithConfig, LinkedTG } from '@/api/types/processedEntities'
import { getMembershipConfigProperty } from '@/helpers/Config'
import { indexBy } from '@/helpers/IndexHelpers'

@Component({
  computed: mapGetters({
    membership: 'Member/membership',
    membershipLevelName: 'Member/membershipLevelName',
    membershipId: 'Member/membershipId',
    membershipScanCode: 'Member/membershipScanCode',
    autoRenewal: 'Member/autoRenewal',
    ticketGroupWithConfig: 'Member/ticketGroupWithConfig',
    eventTemplateWithConfig: 'Member/eventTemplateWithConfig',
  }),
})
export default class MembershipDetailsRoute extends Vue {
  membershipId: string
  membershipScanCode: string
  membership?: MembershipEntity
  membershipLevelName: string
  autoRenewal: AutoRenewState
  savedCard: SavedPaymentMethod | null = null
  ticketGroupWithConfig: LinkedTG
  eventTemplateWithConfig: EventWithConfig
  showEmailSentMessage: boolean = false
  loading: boolean = false
  appleWalletEnabled: boolean = false
  googleWalletEnabled: boolean = false

  created() {
    if (this.hasMembership) {
      this.loading = true
      Promise.all([this.fetchSavedCard(), this.fetchWalletConfig()]).finally(() => {
        this.loading = false
      })
    }
  }

  async fetchSavedCard(): Promise<void> {
    await API.getUncached('my/identity/saved_payment_method').then((resp) => {
      this.savedCard = firstEntity(resp, 'saved_payment_method')?.[0]
    })
  }

  // There are several ambiguities regarding when to display the "Add to Apple/Google Wallet" buttons.
  // For now, we are choosing the simplest yet acceptable approach:
  // show the buttons if their corresponding wallets are configured even if the device doesn't support it.
  //
  // Alternatives we considered:
  //
  // 1. Detect the OS and browser using `navigator.userAgent`.
  //    This approach was rejected because relying on user agent strings is generally discouraged:
  //    they are inconsistent, easily spoofed, and prone to breaking with browser updates.
  //
  // 2. Always display the "Add to Google Wallet" button when Google Wallet is configured,
  //    and show the "Add to Apple Wallet" button only if `window.ApplePaySession` is present.
  //    While this is a reasonable proxy for Apple Wallet support, there are edge cases (e.g., iPhone 5s and earlier)
  //    where Apple Wallet is available but Apple Pay is not.
  //
  // Refer to [this discussion](https://github.com/stqry/tix-web-client/pull/2850#pullrequestreview-2731053870) for more details.
  async fetchWalletConfig(): Promise<void> {
    await API.getUncached(`ticket_order/${this.ticketOrderId}/walletprecheck`).then((response) => {
      const walletPrecheck = indexBy('provider', response.wallet_precheck._data)
      this.appleWalletEnabled = walletPrecheck.apple.ok ?? false
      this.googleWalletEnabled = walletPrecheck.google.ok ?? false
    })
  }

  get renewAllowed() {
    return membershipIsRenewable()
  }

  get expiry(): string {
    const expiry: TixTime = store.getters['Member/membershipExpiry']
    const medium = this.$t('dateFormat.medium') as TTStringFormat
    const formattedExpiry = expiry.format(medium)
    const renewFrom = membershipRenewableFrom()?.format(medium)

    if (!this.renewAllowed && renewFrom) {
      return `${formattedExpiry} (renew from ${renewFrom})`
    } else {
      return formattedExpiry
    }
  }

  get cardIcon() {
    if (!this.savedCard) {
      return null
    }
    const identifier = this.savedCard.identifier
    const brand = identifier.toLowerCase().split('…')[0].replace(/\s/g, '')
    return `/assets/default/images/${brand}.svg`
  }

  get cancelAutoRenewRoute() {
    return `/manage/${this.ticketOrderId}/cancel-auto-renew`
  }

  get autoRenewEnabled() {
    const legacy = environment.config.ask_to_autorenew ? 'prompt' : undefined
    const ticketGroup = this.ticketGroupWithConfig
    const event = this.eventTemplateWithConfig
    const autorenew = getMembershipConfigProperty('renewal', 'automatic', ticketGroup, event, legacy) ?? null
    return autorenew === 'prompt'
  }

  resendMembershipEmail() {
    this.loading = true
    API.post('my/ticket_order/resend', { body: { memberships_only: true } })
      .then(() => {
        this.showEmailSentMessage = true
      })
      .finally(() => {
        this.loading = false
      })
  }

  get hasMembership(): boolean {
    return isCurrentOrPastMember()
  }

  get ticketOrderId(): string {
    return this.membership!.ticket_order_id
  }
}
